/**
 * Meta Marketing API compatibility matrix — frontend mirror of
 * `backend/src/modules/slighter-crm/facebook/shared/meta-matrix.ts`.
 *
 * Keep in sync with the backend file. Both are derived from live testing
 * against Graph API v24.0 in May 2026. Findings are in CLAUDE.md, "Meta Marketing API".
 */

export const ODAX_OBJECTIVES = [
  'OUTCOME_AWARENESS',
  'OUTCOME_TRAFFIC',
  'OUTCOME_ENGAGEMENT',
  'OUTCOME_LEADS',
  'OUTCOME_SALES',
  'OUTCOME_APP_PROMOTION',
] as const;
export type OdaxObjective = typeof ODAX_OBJECTIVES[number];

export const OPT_GOALS_BY_OBJECTIVE: Record<OdaxObjective, string[]> = {
  OUTCOME_AWARENESS:     ['AD_RECALL_LIFT', 'REACH', 'IMPRESSIONS', 'THRUPLAY'],
  OUTCOME_TRAFFIC:       ['LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'IMPRESSIONS', 'REACH'],
  OUTCOME_ENGAGEMENT:    ['POST_ENGAGEMENT', 'IMPRESSIONS', 'REACH', 'CONVERSATIONS',
                          'LINK_CLICKS', 'LANDING_PAGE_VIEWS', 'OFFSITE_CONVERSIONS',
                          'ONSITE_CONVERSIONS', 'THRUPLAY', 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS',
                          'EVENT_RESPONSES'],
  OUTCOME_LEADS:         ['LEAD_GENERATION', 'QUALITY_LEAD', 'OFFSITE_CONVERSIONS',
                          'ONSITE_CONVERSIONS', 'LANDING_PAGE_VIEWS', 'LINK_CLICKS',
                          'IMPRESSIONS', 'REACH', 'QUALITY_CALL',
                          'APP_INSTALLS_AND_OFFSITE_CONVERSIONS'],
  OUTCOME_SALES:         ['OFFSITE_CONVERSIONS', 'VALUE', 'LANDING_PAGE_VIEWS', 'LINK_CLICKS',
                          'IMPRESSIONS', 'REACH', 'CONVERSATIONS'],
  OUTCOME_APP_PROMOTION: ['APP_INSTALLS', 'LINK_CLICKS', 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS', 'VALUE'],
};

export const PIXEL_EVENTS_BY_OBJECTIVE: Record<OdaxObjective, string[]> = {
  OUTCOME_SALES: [
    'PURCHASE', 'COMPLETE_REGISTRATION', 'ADD_PAYMENT_INFO', 'ADD_TO_CART',
    'ADD_TO_WISHLIST', 'SEARCH', 'DONATE', 'START_TRIAL', 'SUBSCRIBE', 'OTHER',
  ],
  OUTCOME_LEADS: [
    'LEAD', 'COMPLETE_REGISTRATION', 'SEARCH', 'CONTACT', 'FIND_LOCATION',
    'SCHEDULE', 'START_TRIAL', 'SUBMIT_APPLICATION', 'SUBSCRIBE', 'OTHER',
  ],
  OUTCOME_ENGAGEMENT: [
    'ADD_TO_WISHLIST', 'SEARCH', 'CONTACT', 'CUSTOMIZE_PRODUCT', 'DONATE',
    'FIND_LOCATION', 'SCHEDULE', 'START_TRIAL', 'SUBMIT_APPLICATION', 'SUBSCRIBE',
    'OTHER',
  ],
  OUTCOME_AWARENESS:     [],
  OUTCOME_TRAFFIC:       [],
  OUTCOME_APP_PROMOTION: [],
};

export const CTA_TYPES_LINK_DATA = [
  'NO_BUTTON', 'LEARN_MORE', 'SHOP_NOW', 'BOOK_TRAVEL', 'SIGN_UP', 'DOWNLOAD',
  'INSTALL_APP', 'INSTALL_MOBILE_APP', 'USE_APP', 'USE_MOBILE_APP',
  'ORDER_NOW', 'SUBSCRIBE', 'APPLY_NOW', 'CONTACT_US', 'GET_QUOTE', 'GET_OFFER',
  'DONATE', 'DONATE_NOW', 'WATCH_MORE', 'LISTEN_MUSIC', 'PLAY_GAME',
  'WHATSAPP_MESSAGE', 'MESSAGE_PAGE', 'GET_SHOWTIMES', 'REQUEST_TIME', 'SAVE',
  'BUY_TICKETS', 'OPEN_LINK', 'BUY_NOW', 'SEE_DETAILS', 'SEE_MORE', 'GO_LIVE',
  'INTERESTED',
] as const;

export const CTA_TYPES_SPECIAL_VALUE = ['CALL_NOW', 'GET_DIRECTIONS', 'SEND_MESSAGE'] as const;

export interface PlacementRule { position: string; requires: string[]; description: string; }
export const PLACEMENT_REQUIRES_FEED: PlacementRule[] = [
  { position: 'facebook_positions:story',          requires: ['facebook_positions:feed', 'instagram_positions:story'], description: 'FB Stories needs FB Feed or IG Stories' },
  { position: 'facebook_positions:marketplace',    requires: ['facebook_positions:feed'], description: 'FB Marketplace needs FB Feed' },
  { position: 'facebook_positions:instream_video', requires: ['facebook_positions:feed'], description: 'FB In-stream needs FB Feed' },
  { position: 'facebook_positions:search',         requires: ['facebook_positions:feed'], description: 'FB Search needs FB Feed' },
  { position: 'facebook_positions:biz_disco_feed', requires: ['facebook_positions:feed'], description: 'FB Business Explore needs FB Feed' },
  { position: 'instagram_positions:explore',       requires: ['instagram_positions:stream'], description: 'IG Explore needs IG Feed' },
  { position: 'instagram_positions:explore_home',  requires: ['instagram_positions:explore'], description: 'IG Explore Home needs IG Explore' },
  { position: 'instagram_positions:ig_search',     requires: ['instagram_positions:stream'], description: 'IG Search needs IG Feed' },
  { position: 'messenger_positions:story',         requires: ['facebook_positions:feed', 'instagram_positions:story'], description: 'Messenger Stories needs FB Feed or IG Stories' },
  { position: 'audience_network_positions:classic',        requires: ['facebook_positions:feed'], description: 'AN Classic needs FB Feed' },
  { position: 'audience_network_positions:rewarded_video', requires: ['facebook_positions:feed'], description: 'AN Rewarded Video needs FB Feed' },
];

export const DESTINATION_TYPES = ['WEBSITE', 'APP', 'MESSENGER', 'WHATSAPP', 'INSTAGRAM_DIRECT', 'ON_AD', 'ON_POST', 'ON_PAGE', 'ON_EVENT', 'ON_VIDEO'] as const;
export type DestinationType = typeof DESTINATION_TYPES[number];

// ── Billing events per optimization goal ─────────────────────────────────────
// Meta validates (optimization_goal, billing_event) atomically, and the error
// is cryptic ("The specified billing event is not a valid option…"). A static
// dropdown offered pairings Meta rejects — e.g. POST_ENGAGEMENT billing with
// the POST_ENGAGEMENT goal failed live (2026-08-28) and the old error blamed
// the Page instead of the billing event.
//
// Live evidence per pairing:
//   IMPRESSIONS  — passed with every goal in the 2026-08-28 matrix runs.
//   LINK_CLICKS  — passed with the LINK_CLICKS goal (Traffic/Engagement/Leads/Sales).
//   THRUPLAY     — the THRUPLAY/THRUPLAY ad set saved (later step failed on an
//                  unrelated thumbnail issue), so the pairing itself is valid.
// Goals not listed here fall back to IMPRESSIONS only — the one billing event
// Meta accepts everywhere. Widen a goal's list only with a live PASS behind it.
const BILLING_LABEL: Record<string, string> = {
  IMPRESSIONS: 'Impressions',
  LINK_CLICKS: 'Link clicks',
  THRUPLAY: 'ThruPlay (video)',
};

const EXTRA_BILLING_BY_GOAL: Record<string, string[]> = {
  LINK_CLICKS: ['LINK_CLICKS'],
  THRUPLAY: ['THRUPLAY'],
};

/** Billing events that are safe to offer for a goal. IMPRESSIONS always first. */
export function billingEventsForGoal(goal: string): Array<{ code: string; label: string }> {
  const codes = ['IMPRESSIONS', ...(EXTRA_BILLING_BY_GOAL[goal] ?? [])];
  return codes.map((code) => ({ code, label: BILLING_LABEL[code] ?? code }));
}

// ── Verification notices ─────────────────────────────────────────────────────
// Surfaced under the matching option in the workspace so nobody discovers a
// gap at publish time. Two kinds:
//   'account'  — the CRM side works, but Meta gates the option on account
//                state (pixel history, app events). Same class as TikTok's
//                Conversions warning.
//   'untested' — never exercised against the live API from this CRM; may
//                work, but treat results with care until a live PASS lands.
// Full matrix run 2026-08-28: 25/27 goal chains verified. Keep this in sync
// with CLAUDE.md when a notice is cleared.
export interface OptionNotice { kind: 'account' | 'untested'; text: string }

export const GOAL_NOTICES: Record<string, OptionNotice> = {
  'OUTCOME_SALES:VALUE': {
    kind: 'account',
    text: 'Needs pixel setup — Meta only allows value optimisation once your pixel reports purchase events that include a value. New pixels are rejected.',
  },
  'OUTCOME_APP_PROMOTION:VALUE': {
    kind: 'account',
    text: 'Needs app purchase events — Meta requires your app to report purchases (via SDK or a measurement partner) before value optimisation is allowed.',
  },
  // Location-specific: website conversions work, app conversions need the event logged by the app
  // ("The custom app event you're trying to optimize for is currently not actively logged", live 2026-09-29).
  'OUTCOME_SALES:APP:OFFSITE_CONVERSIONS': {
    kind: 'account',
    text: 'Needs app events — Meta only optimises for an in-app event your app already logs (via the SDK or a measurement partner).',
  },
};

export function goalNotice(objective: string | undefined, goal: string, location?: string): OptionNotice | undefined {
  if (!objective) return undefined;
  return (location ? GOAL_NOTICES[`${objective}:${location}:${goal}`] : undefined) ?? GOAL_NOTICES[`${objective}:${goal}`];
}

/**
 * Bid strategies not yet exercised live from this CRM. Emptied 2026-08-28:
 * bid cap and cost cap both passed the live sweep (bid cap also through the
 * UI end-to-end), alongside CBO daily/lifetime. Add a code here (and it gets
 * a "Not live-tested yet" tile label) only when offering something unproven.
 */
export const UNTESTED_BID_STRATEGIES: readonly string[] = [];

export const BUDGET_FLOORS: Record<string, { daily: number; description: string }> = {
  default:                 { daily: 100,  description: 'Default minimum ad set daily budget' },
  'destination:WHATSAPP':  { daily: 1107, description: 'WhatsApp destination requires ~AED 11.07/day' },
  'destination:MESSENGER': { daily: 1107, description: 'Messenger destination requires ~AED 11.07/day' },
};

export const FREQ_CAP_COMPATIBLE_GOALS = ['REACH', 'AD_RECALL_LIFT', 'IMPRESSIONS'] as const;

// Meta refuses these goals under a COST_CAP bid strategy (CBO or ABO): "You are using wrong optimization
// goal with cost cap" — CONVERSATIONS, live 2026-09-30 (bid cap and lowest cost pass). Widen only with a live FAIL.
export const COST_CAP_REFUSED_GOALS: readonly string[] = ['CONVERSATIONS'];

// CREDIT was retired by Meta (live 2026-09-30) in favour of FINANCIAL_PRODUCTS_SERVICES.
export const SPECIAL_AD_CATEGORIES = ['NONE', 'EMPLOYMENT', 'HOUSING', 'FINANCIAL_PRODUCTS_SERVICES', 'ISSUES_ELECTIONS_POLITICS', 'ONLINE_GAMBLING_AND_GAMING'] as const;
export type SpecialAdCategory = typeof SPECIAL_AD_CATEGORIES[number];

export const ADVANTAGE_AUDIENCE_AGE_MAX = 65;

export const BUYING_TYPES = ['AUCTION', 'RESERVED'] as const;

export const BID_STRATEGIES = ['LOWEST_COST_WITHOUT_CAP', 'LOWEST_COST_WITH_BID_CAP', 'COST_CAP'] as const;
export const BID_STRATEGIES_REQUIRING_BID_AMOUNT = ['LOWEST_COST_WITH_BID_CAP', 'COST_CAP'] as const;

// ── Helpers ─────────────────────────────────────────────────────────────────

export function optGoalsForObjective(objective: OdaxObjective): string[] {
  return OPT_GOALS_BY_OBJECTIVE[objective] ?? [];
}

export function pixelEventsForObjective(objective: OdaxObjective): string[] {
  return PIXEL_EVENTS_BY_OBJECTIVE[objective] ?? [];
}

export function isOptGoalValidForObjective(goal: string, objective: OdaxObjective): boolean {
  return optGoalsForObjective(objective).includes(goal);
}

export function budgetFloorFor(destinationType?: string): number {
  if (destinationType && BUDGET_FLOORS[`destination:${destinationType}`]) {
    return BUDGET_FLOORS[`destination:${destinationType}`].daily;
  }
  return BUDGET_FLOORS.default.daily;
}

export interface PlacementInput {
  facebook_positions?: string[];
  instagram_positions?: string[];
  messenger_positions?: string[];
  audience_network_positions?: string[];
}

/**
 * Returns the first violated cascade rule, or null if every enabled
 * sub-placement has at least one of its required peers also enabled.
 * Use in the form's `disabled`/`hint` logic to prevent invalid submits.
 */
export function checkPlacementCascade(targeting: PlacementInput): PlacementRule | null {
  const enabled = new Set<string>();
  for (const [arr, prefix] of [
    [targeting.facebook_positions,         'facebook_positions'],
    [targeting.instagram_positions,        'instagram_positions'],
    [targeting.messenger_positions,        'messenger_positions'],
    [targeting.audience_network_positions, 'audience_network_positions'],
  ] as const) {
    for (const p of arr ?? []) enabled.add(`${prefix}:${p}`);
  }
  for (const rule of PLACEMENT_REQUIRES_FEED) {
    if (!enabled.has(rule.position)) continue;
    if (!rule.requires.some(r => enabled.has(r))) return rule;
  }
  return null;
}

/** Human-readable label for an optimization goal (UI display only). */
export function goalLabel(goal: string): string {
  return goal.split('_').map(s => s.charAt(0) + s.slice(1).toLowerCase()).join(' ');
}

/** Human-readable label for an ODAX objective. */
export function objectiveLabel(o: OdaxObjective): string {
  const map: Record<OdaxObjective, string> = {
    OUTCOME_AWARENESS:     'Awareness',
    OUTCOME_TRAFFIC:       'Traffic',
    OUTCOME_ENGAGEMENT:    'Engagement',
    OUTCOME_LEADS:         'Leads',
    OUTCOME_SALES:         'Sales',
    OUTCOME_APP_PROMOTION: 'App Promotion',
  };
  return map[o];
}
